import { useEffect, useState } from 'react';
import { api, type Mes as DatosMes, type Persona, type Yo } from '../services/api';
import { Aviso, Cargando, CoronaEl1, Tarjeta, Titulo, Vacio } from '../components/ui';
import { PlacaMes } from '../components/PlacaMes';
import { AyudaContextual } from '../components/AyudaContextual';
import { CierreMes } from '../components/CierreMes';
import { fechaHora, hoy, limitesDelMes, mesEnPalabras } from '../nucleo/fechas';

/** 'el jueves 1/10 a las 06:00' */
function cuandoCierra(segundos: number): string {
  const { dia, hora } = fechaHora(segundos);
  return `el ${dia} a las ${hora}`;
}

/**
 * El mes: la tabla por puntos y los tres reconocimientos.
 *
 * Tres y no uno, a propósito. El Elo premia al que SORPRENDE, no al que gana:
 * el mejor del grupo es favorito siempre, así que ganando todo casi no sube y
 * por Elo nunca sería campeón. Con tres reconocimientos el mes tiene tres
 * ganadores posibles y nadie siente que la métrica está armada en su contra.
 */
export function Mes({
  yo,
  onVerPerfil,
  onVerResumen,
}: {
  yo: Yo;
  onVerPerfil: (id: number) => void;
  onVerResumen: (mes: string) => void;
}) {
  const [cual, setCual] = useState<string | undefined>(undefined);
  const [datos, setDatos] = useState<DatosMes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viendoPlaca, setViendoPlaca] = useState(false);

  useEffect(() => {
    setDatos(null);
    setViendoPlaca(false);
    api
      .mes(cual)
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, [cual]);

  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return <Cargando que="Calculando el mes" />;

  const { reconocimientos: r } = datos;
  // El mes actual puede no tener partidos todavía y faltar en meses_con_datos.
  // Siempre debe existir la opción que corresponde a la tabla visible y la de volver a hoy.
  const mesActual = hoy().slice(0, 7);
  const mesesDisponibles = [...new Set([mesActual, datos.mes, ...datos.meses_con_datos])]
    .sort((a, b) => b.localeCompare(a));
  const hayDatos = datos.tabla.length > 0;
  // La placa sale con el mes cerrado: antes el campeón todavía puede cambiar.
  // El admin la ve antes, marcada "provisoria", para mirar cómo queda.
  const hayPlaca = hayDatos && (datos.cerrado || yo.jugador.rol === 'admin');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <div className="min-w-0">
            <Titulo>{datos.titulo}</Titulo>
            <p className="text-xs text-(--color-apagado)">{datos.cerrado ? 'Mes cerrado' : hoy() > limitesDelMes(datos.mes).hasta ? 'Por cerrar' : 'En curso'}</p>
          </div>
          <AyudaContextual titulo="Tabla del mes">
            <div className="flex flex-col gap-3">
              {!datos.cerrado && <p>
                {hoy() > limitesDelMes(datos.mes).hasta
                  ? `El mes terminó y cierra ${cuandoCierra(datos.cierra_en)}: hasta entonces se pueden cargar los partidos del último día. Los pendientes se confirman solos al cerrar.`
                  : `El mes está en curso y cierra ${cuandoCierra(datos.cierra_en)}. Hasta entonces, esto puede cambiar.`}
              </p>}
              <p>Los puntos del mes son <strong>3 por partido ganado y 1 por jugado</strong> — el 1
                premia venir. Es distinto de Nivel, que va por Elo: ahí importa
                <em> contra quién</em> ganaste. Para entrar en la corona hay que haber jugado un
                mínimo de partidos en el mes.</p>
              <p><strong>👑 Campeón</strong>: el que más puntos hizo.{' '}
                <strong>📈 Más subió</strong>: el que más superó lo que se esperaba de él.{' '}
                <strong>🎾 Más presente</strong>: el que más partidos jugó.</p>
            </div>
          </AyudaContextual>
        </div>
        {mesesDisponibles.length > 1 && (
          <label
            className="relative ml-auto flex min-h-11 shrink-0 cursor-pointer items-center rounded-lg border
              border-(--color-borde) bg-(--color-panel) px-3 text-right text-sm
              focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-(--color-acento)"
          >
            <span>Seleccionar otro mes</span>
            {/* El select cubre el botón para abrir el selector nativo también en iPhone. */}
            <select
              aria-label="Seleccionar otro mes"
              value={datos.mes}
              onChange={(e) => setCual(e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
              {mesesDisponibles.map((m) => (
                <option key={m} value={m}>
                  {mesEnPalabras(m)}{m === mesActual ? ' (mes en curso)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {hayDatos && !datos.cerrado && yo.jugador.rol === 'admin' && datos.mes <= hoy().slice(0, 7) && (
        <CierreMes key={datos.mes} datos={datos} onCerrado={mes => {
          setViendoPlaca(false);
          setDatos(mes);
        }} />
      )}

      {!hayDatos ? (
        <Vacio>
          Todavía no hay partidos en este mes. El primero que carguen abre la
          tabla.
        </Vacio>
      ) : (
        <>
          {/* Los tres en una sola tarjeta, un renglón cada uno: como tres
              tarjetas ocupaban media pantalla del celular y empujaban la
              tabla, que es lo que se viene a mirar. El porqué de cada uno
              está en la ayuda junto al título del mes. */}
          {(r.campeones.length > 0 || r.mas_subio.length > 0 || r.mas_presente.length > 0) && (
            <Tarjeta compacta className="flex flex-col">
              <Reconocimiento
                icono="👑"
                titulo={r.campeones.length > 1 ? 'Campeones' : 'Campeón'}
                gente={r.campeones}
                yoId={yo.jugador.id}
                destacado
              />
              <Reconocimiento icono="📈" titulo="Más subió" gente={r.mas_subio} yoId={yo.jugador.id} />
              <Reconocimiento
                icono="🎾"
                titulo="Más presente"
                gente={r.mas_presente}
                yoId={yo.jugador.id}
              />
            </Tarjeta>
          )}

          {hayPlaca && (
            <button
              onClick={() => onVerResumen(datos.mes)}
              className="rounded-xl bg-(--color-acento) py-2.5 text-center font-semibold
                text-(--color-sobre-acento) transition active:scale-[0.98]"
            >
              📋 Resumen del mes y tu mes{datos.cerrado ? '' : ' (provisorio)'}
            </button>
          )}
          {hayPlaca && viendoPlaca && (
            <PlacaMes mes={datos.mes} titulo={datos.titulo} onCerrar={() => setViendoPlaca(false)} />
          )}
          {hayPlaca && !viendoPlaca && (
            <button
              onClick={() => setViendoPlaca(true)}
              className="rounded-xl border border-(--color-acento) bg-(--color-panel) py-2.5 text-center
                font-semibold transition active:scale-[0.98]"
            >
              📸 Placa del mes{datos.cerrado ? '' : ' (provisoria, solo admin)'}
            </button>
          )}

          <Tarjeta sinRelleno>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-(--color-heroe) text-[11px] uppercase tracking-wide">
                  <th className="py-2 pl-3 text-left font-normal">#</th>
                  <th className="py-2 text-left font-normal">Jugador</th>
                  <th className="py-2 text-right font-normal">PJ</th>
                  <th className="py-2 text-right font-normal">G-P</th>
                  <th className="py-2 text-right font-normal">Dif</th>
                  <th className="py-2 pr-3 text-right font-normal">Pts</th>
                </tr>
              </thead>
              <tbody>
                {datos.tabla.map((f) => (
                  <tr
                    key={f.id}
                    onClick={() => onVerPerfil(f.id)}
                    className={`cursor-pointer border-b border-(--color-borde)/40 last:border-0 active:bg-white/10
                      ${f.id === yo.jugador.id ? 'bg-white/5' : ''}
                      ${f.elegible ? '' : 'opacity-50'}`}
                  >
                    <td className="py-2 pl-3 font-titulo text-2xl tabular-nums">
                      {f.elegible ? f.posicion : '–'}
                    </td>
                    <td className="py-3 underline decoration-(--color-borde) underline-offset-2">
                      {f.nombre}
                      {f.id === datos.el_1 && <CoronaEl1 />}
                      {!f.elegible && (
                        <span
                          className="ml-2 text-[10px] text-(--color-apagado)"
                          title="Jugó pocos partidos este mes: no entra en la corona"
                        >
                          pocos partidos
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {f.partidos}
                    </td>
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {f.ganados}-{f.perdidos}
                    </td>
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {f.games_favor - f.games_contra > 0 ? '+' : ''}
                      {f.games_favor - f.games_contra}
                    </td>
                    <td className="py-3 pr-3 text-right font-semibold tabular-nums">
                      {f.puntos}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Tarjeta>

        </>
      )}
    </div>
  );
}

/** ["A"] -> "A" · ["A","B"] -> "A y B" · ["A","B","C"] -> "A, B y C" */
function enCastellano(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** Un renglón de la tarjeta de reconocimientos. */
function Reconocimiento({
  icono,
  titulo,
  gente,
  yoId,
  destacado,
}: {
  icono: string;
  titulo: string;
  gente: Persona[];
  yoId: number;
  destacado?: boolean;
}) {
  if (gente.length === 0) return null;

  const soyYo = gente.some((p) => p.id === yoId);

  return (
    <div className="flex items-center gap-2 border-b border-(--color-borde)/40 py-1.5 last:border-0">
      <span className="w-6 text-center text-lg">{icono}</span>
      <span className="shrink-0 text-xs text-(--color-apagado)">{titulo}</span>
      <span
        className={`ml-auto min-w-0 text-right font-semibold
          ${destacado ? 'text-(--color-acento)' : ''}`}
      >
        {enCastellano(gente.map((p) => p.nombre))}
        {soyYo && <span className="ml-1.5 text-xs font-normal text-(--color-apagado)">vos</span>}
      </span>
    </div>
  );
}
